import { useEffect, useRef, useState, type FC } from "react";
import { Link } from "react-router-dom";
import { navigateToCategory } from "../../utils/navigation";
import {
  Menu,
  ChevronDown,
  ChevronRight,
  Loader2,
  Package,
} from "lucide-react";
import type { CategoriesProps } from "../../types/Categories";

// Recursive component to render nested subcategories with auto-expand on hover
interface NestedSubCategoryProps {
  subCategory: any;
  parentCategoryId: number;
  level?: number;
  onClose: () => void;
  depth?: number;
}

const NestedSubCategory: FC<NestedSubCategoryProps> = ({
  subCategory,
  parentCategoryId,
  level = 0,
  onClose,
  depth = 0,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const leaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const hasChildren =
    subCategory.sub_categories && subCategory.sub_categories.length > 0;

  // Auto-expand on hover after a short delay
  const handleMouseEnter = () => {
    setIsHovering(true);

    // Clear any pending collapse timeout
    if (leaveTimeoutRef.current) {
      clearTimeout(leaveTimeoutRef.current);
      leaveTimeoutRef.current = null;
    }

    if (hasChildren && !isExpanded) {
      hoverTimeoutRef.current = setTimeout(() => {
        setIsExpanded(true);
      }, 200); // 200ms delay for smooth experience
    }
  };

  const handleMouseLeave = (e: React.MouseEvent) => {
    // Check if we're moving to a child element
    const relatedTarget = e.relatedTarget as Node;
    if (
      containerRef.current &&
      relatedTarget &&
      containerRef.current.contains(relatedTarget)
    ) {
      // Mouse moved to a child element, don't collapse
      return;
    }

    setIsHovering(false);

    // Clear expand timeout if still pending
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }

    // Set a timeout to collapse after leaving
    if (hasChildren && isExpanded) {
      leaveTimeoutRef.current = setTimeout(() => {
        setIsExpanded(false);
      }, 300); // 300ms delay before collapsing
    }
  };

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
      if (leaveTimeoutRef.current) {
        clearTimeout(leaveTimeoutRef.current);
      }
    };
  }, []);

  // Color schemes for different levels
  const getLevelColors = (lvl: number) => {
    const colors = [
      {
        bg: "from-neutral-800 to-neutral-700",
        hover: "from-neutral-700 to-neutral-600",
        text: "text-primary",
      },
      {
        bg: "from-neutral-800 to-neutral-700",
        hover: "from-neutral-700 to-neutral-600",
        text: "text-primary",
      },
      {
        bg: "from-neutral-800 to-neutral-700",
        hover: "from-neutral-700 to-neutral-600",
        text: "text-primary",
      },
      {
        bg: "from-neutral-800 to-neutral-700",
        hover: "from-neutral-700 to-neutral-600",
        text: "text-primary",
      },
    ];
    return colors[lvl % colors.length];
  };

  const levelColors = getLevelColors(level);

  return (
    <div
      ref={containerRef}
      className={`${level > 0 ? "ml-3" : ""}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div
        className={`flex items-center justify-between p-2.5 mb-1 rounded-lg transition-all duration-200 group cursor-pointer
          ${isHovering ? "bg-neutral-800 shadow-md scale-[1.02]" : "hover:bg-neutral-800"}
          ${level === 0 ? "border border-neutral-800" : "border border-transparent hover:border-neutral-800"}
        `}
      >
        <Link
          to={navigateToCategory(subCategory.id)}
          className="flex-1 flex items-center space-x-2.5"
          onClick={onClose}
        >
          {/* Icon with level-based styling */}
          <div
            className={`shrink-0 ${level === 0 ? "w-9 h-9" : level === 1 ? "w-7 h-7" : "w-6 h-6"} 
            bg-linear-to-br ${isHovering ? levelColors.hover : levelColors.bg} 
            rounded-lg flex items-center justify-center shadow-sm transition-all duration-200
            ${isHovering ? "scale-110 rotate-3" : ""}
          `}
          >
            {level === 0 ? (
              <Package className={`w-4 h-4 ${levelColors.text}`} />
            ) : (
              <span
                className={`font-bold ${level === 0 ? "text-xs" : "text-[10px]"} ${levelColors.text}`}
              >
                {subCategory.name.charAt(0).toUpperCase()}
              </span>
            )}
          </div>

          {/* Text content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h5
                className={`font-semibold ${level === 0 ? "text-sm" : level === 1 ? "text-xs" : "text-[11px]"} 
                ${isHovering ? "text-primary" : "text-neutral-300"} 
                transition-colors duration-200 truncate`}
              >
                {subCategory.name}
              </h5>
              {hasChildren && (
                <span className="text-[9px] bg-neutral-700 text-neutral-400 px-1.5 py-0.5 rounded-full font-medium">
                  {subCategory.sub_categories.length}
                </span>
              )}
            </div>
            {subCategory.description && level === 0 && (
              <p className="text-xs text-neutral-500 mt-0.5 line-clamp-1">
                {subCategory.description}
              </p>
            )}
          </div>
        </Link>

        {/* Expand indicator */}
        {hasChildren && (
          <div className="flex items-center space-x-1">
            <ChevronRight
              className={`w-4 h-4 transition-all duration-300 ${
                isHovering ? "text-primary" : "text-neutral-500"
              } ${isExpanded ? "rotate-90" : ""}`}
            />
          </div>
        )}
      </div>

      {/* Nested children with smooth animation */}
      {hasChildren && isExpanded && (
        <div
          className={`ml-2 border-l-2 ${isHovering ? "border-primary/50" : "border-neutral-700"} pl-3 mt-1 space-y-0.5
          animate-in slide-in-from-left-1 duration-200`}
        >
          {subCategory.sub_categories.map((nestedSub: any) => (
            <NestedSubCategory
              key={nestedSub.id}
              subCategory={nestedSub}
              parentCategoryId={parentCategoryId}
              level={level + 1}
              depth={depth + 1}
              onClose={onClose}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const Categories: FC<CategoriesProps> = ({
  categoriesLoading,
  categoriesError,
  categoriesWithSubs,
}) => {
  const [isCategoriesOpen, setIsCategoriesOpen] = useState(false);
  const [isCategoriesVisible, setIsCategoriesVisible] = useState(false);
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const displayCategories = categoriesWithSubs || [];

  // Set first category as active by default when categories load
  useEffect(() => {
    if (displayCategories.length > 0 && !activeCategory) {
      setActiveCategory(displayCategories[0].id.toString());
    }
  }, [displayCategories, activeCategory]);

  // Animated close for categories dropdown
  const handleCategoriesMouseEnter = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setIsCategoriesOpen(true);
    setIsCategoriesVisible(true);
  };

  const handleCategoriesMouseLeave = () => {
    closeTimeoutRef.current = setTimeout(() => {
      setIsCategoriesOpen(false);
      setTimeout(() => setIsCategoriesVisible(false), 100);
    }, 100); // 0.5 second delay
  };
  const newLocal = "flex max-w-[1400px] mx-auto";
  return (
    <div className="bg-neutral-900 border-b border-neutral-800 relative">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center space-x-8">
          {/* All Categories Dropdown */}
          <div
            className=""
            onMouseEnter={handleCategoriesMouseEnter}
            onMouseLeave={handleCategoriesMouseLeave}
          >
            <Link
              to="/products"
              className="flex items-center space-x-2 bg-linear-to-r from-primary-dark to-primary text-black px-6 py-3 rounded-lg hover:from-primary-darker hover:to-primary-dark transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              <Menu className="w-5 h-5" />
              <span className="font-semibold">All Categories</span>
              <ChevronDown
                className={`w-4 h-4 transition-transform duration-200 ${
                  isCategoriesOpen ? "rotate-180" : ""
                }`}
              />
            </Link>

            {/* Categories Dropdown - Full Width Mega Menu */}
            {isCategoriesVisible && (
              <div
                className={`absolute top-full left-0 right-0 bg-neutral-900 border border-neutral-800 shadow-2xl z-50 transition-opacity duration-300 ${
                  isCategoriesOpen
                    ? "opacity-100"
                    : "opacity-0 pointer-events-none"
                }`}
                onMouseEnter={handleCategoriesMouseEnter}
                onMouseLeave={handleCategoriesMouseLeave}
              >
                {categoriesLoading ? (
                  <div className="flex items-center justify-center p-12">
                    <Loader2 className="w-8 h-8 animate-spin text-primary" />
                    <span className="ml-3 text-neutral-400 font-medium">
                      Loading categories...
                    </span>
                  </div>
                ) : categoriesError ? (
                  <div className="flex items-center justify-center p-12 text-red-500">
                    <div className="text-center">
                      <div className="text-4xl mb-2">⚠️</div>
                      <span className="font-medium">
                        Failed to load categories
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="flex max-w-350 mx-auto min-h-112.5">
                    {/* Left Side: Main Categories - 1/3 width */}
                    <div className="w-1/2 border-r border-neutral-800 bg-neutral-900">
                      <div className="p-3 border-b border-neutral-800 bg-primary-dark">
                        <h3 className="font-semibold text-white text-sm">
                          All Categories
                        </h3>
                      </div>
                      <div className="max-h-103.75 overflow-y-auto">
                        {displayCategories.map((category) => (
                          <div
                            key={category.id}
                            className={`px-3 py-2.5 cursor-pointer transition-all duration-200 border-b border-neutral-800 ${
                              activeCategory === category.id.toString()
                                ? "bg-neutral-800 text-primary border-l-3 border-l-primary"
                                : "hover:bg-neutral-800 text-neutral-300"
                            }`}
                            onMouseEnter={() =>
                              setActiveCategory(category.id.toString())
                            }
                          >
                            <div className="flex items-center justify-between">
                              <Link
                                to={navigateToCategory(category.id)}
                                className="flex-1 flex items-center space-x-2"
                                onClick={() => {
                                  setIsCategoriesOpen(false);
                                  setActiveCategory(null);
                                }}
                              >
                                {category.icon_path && (
                                  <div className="shrink-0 w-8 h-8 bg-neutral-800 rounded-lg border border-neutral-700 flex items-center justify-center p-1">
                                    <img
                                      src={`https://gzvhulvbgyymgjgdalpa.supabase.co/storage/v1/object/public/publicimages/category_images/${category.icon_path}`}
                                      alt={category.name}
                                      className="w-full h-full object-contain"
                                    />
                                  </div>
                                )}
                                <span className="font-medium text-sm">
                                  {category.name}
                                </span>
                              </Link>
                              {category.sub_categories?.length ? (
                                <ChevronRight
                                  className={`w-4 h-4 ${
                                    activeCategory === category.id.toString()
                                      ? "text-primary"
                                      : "text-neutral-500"
                                  }`}
                                />
                              ) : null}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Middle: Nested Subcategories - 1/3 width */}
                    <div className="w-1/2 bg-neutral-800 border-r border-neutral-700">
                      {activeCategory ? (
                        <div className="h-full">
                          {(() => {
                            const selectedCategory = displayCategories.find(
                              (cat) => cat.id.toString() === activeCategory,
                            );

                            return (
                              <>
                                <div className="p-4 border-b border-neutral-700 bg-neutral-900">
                                  <div className="flex items-center justify-between">
                                    <h4 className="font-semibold text-white">
                                      {selectedCategory?.name}
                                    </h4>
                                    <Link
                                      to={navigateToCategory(
                                        selectedCategory?.id || 0,
                                      )}
                                      className="px-3 py-1.5 bg-primary text-black text-xs font-medium rounded hover:bg-primary-dark transition-colors"
                                      onClick={() => {
                                        setIsCategoriesOpen(false);
                                        setActiveCategory(null);
                                      }}
                                    >
                                      View All
                                    </Link>
                                  </div>
                                </div>

                                <div className="max-h-95 overflow-y-auto p-4">
                                  {selectedCategory?.sub_categories?.length ? (
                                    <div className="space-y-1">
                                      {selectedCategory.sub_categories.map(
                                        (subcat: any) => (
                                          <NestedSubCategory
                                            key={subcat.id}
                                            subCategory={subcat}
                                            parentCategoryId={
                                              selectedCategory.id
                                            }
                                            onClose={() => {
                                              setIsCategoriesOpen(false);
                                              setActiveCategory(null);
                                            }}
                                          />
                                        ),
                                      )}
                                    </div>
                                  ) : (
                                    <div className="p-8 text-center text-neutral-500">
                                      <Package className="w-12 h-12 mx-auto mb-2 text-neutral-600" />
                                      <p className="text-sm">
                                        No subcategories
                                      </p>
                                    </div>
                                  )}
                                </div>
                              </>
                            );
                          })()}
                        </div>
                      ) : (
                        <div className="h-full flex items-center justify-center text-gray-400">
                          <div className="text-center">
                            <ChevronRight className="w-16 h-16 mx-auto mb-3 text-neutral-600" />
                            <p className="text-sm">Hover over a category</p>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Right: Promotions - 1/3 width */}
                    {/* <div className="w-1/3 bg-gradient-to-br from-neutral-800 to-neutral-900 p-4">
                      <h4 className="font-semibold text-neutral-400 text-xs mb-3">
                        Hot Deals
                      </h4>
                      <div className="grid grid-cols-2 gap-2">
                        {promotions.slice(0, 4).map((promo, index) => (
                          <div
                            key={index}
                            className={`relative bg-gradient-to-br ${promo.gradient} rounded-lg overflow-hidden shadow hover:shadow-md transition-all cursor-pointer group h-24`}
                          >
                            <div className="absolute inset-0 opacity-10">
                              <img
                                src={promo.src}
                                alt={promo.title}
                                className="w-full h-full object-cover"
                              />
                            </div>

                            <div className="relative h-full p-2 flex flex-col justify-between">
                              <span
                                className={`${promo.badgeColor} text-[9px] font-bold px-1.5 py-0.5 rounded-full self-end`}
                              >
                                {promo.badge}
                              </span>

                              <div className={promo.textColor}>
                                <h5 className="font-bold text-[10px] leading-tight mb-0.5">
                                  {promo.title}
                                </h5>
                                <p className="text-sm font-extrabold leading-tight">
                                  {promo.subtitle}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div> */}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Quick Navigation Links */}
          <nav className="flex items-center py-3">
            {displayCategories.slice(0, 6).map((category) => (
              <div key={category.id} className="group">
                <div className="inline-block pr-4 text-sm">
                  <Link
                    to={navigateToCategory(category.id)}
                    className="w-full text-neutral-300 hover:text-primary font-medium transition-colors inline-flex items-center space-x-1.5"
                  >
                    {/* {category.icon_path && (
                      <div className="w-5 h-5 flex-shrink-0">
                        <img 
                          src={`https://gzvhulvbgyymgjgdalpa.supabase.co/storage/v1/object/public/publicimages/category_images/${category.icon_path}`}
                          alt={category.name}
                          className="w-full h-full object-contain opacity-70 group-hover:opacity-100 transition-opacity"
                        />
                      </div>
                    )} */}
                    <span>{category.name}</span>
                    {category.sub_categories?.length ? (
                      <ChevronDown className="w-3 h-3 text-neutral-500 transition-transform duration-100 group-hover:rotate-180" />
                    ) : null}
                  </Link>
                </div>

                {category.sub_categories?.length ? (
                  <div
                    className="absolute left-0 right-0 top-full bg-neutral-900 border border-neutral-800 shadow-2xl z-50 
                    opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200"
                  >
                    <div className={newLocal}>
                      {/* Subcategories Panel - 2/3 width */}
                      <div className="w-full bg-neutral-900 border-r border-neutral-800">
                        <div className="h-full">
                          <div className="p-3 border-b border-neutral-800 bg-neutral-800">
                            <h4 className="font-semibold text-white text-sm">
                              {category.name}
                            </h4>
                          </div>

                          <div className="max-h-87.5 overflow-y-auto overflow-x-hidden p-3">
                            <div className="space-y-1">
                              {category.sub_categories.map((subcat: any) => (
                                <NestedSubCategory
                                  key={subcat.id}
                                  subCategory={subcat}
                                  parentCategoryId={category.id}
                                  onClose={() => {}}
                                />
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Promotions Panel - 1/3 width */}
                      {/* <div className="w-1/3 bg-gradient-to-br from-neutral-800 to-neutral-900 p-4">
                        <h4 className="font-semibold text-neutral-400 text-xs mb-3">
                          Hot Deals
                        </h4>
                        <div className="grid grid-cols-2 gap-2">
                          {promotions.slice(0, 4).map((promo, index) => (
                            <div
                              key={index}
                              className={`relative bg-gradient-to-br ${promo.gradient} rounded-lg overflow-hidden shadow hover:shadow-md transition-all cursor-pointer group h-24`}
                            >
                              <div className="absolute inset-0 opacity-10">
                                <img
                                  src={promo.src}
                                  alt={promo.title}
                                  className="w-full h-full object-cover"
                                />
                              </div>

                              <div className="relative h-full p-2 flex flex-col justify-between">
                                <span
                                  className={`${promo.badgeColor} text-[9px] font-bold px-1.5 py-0.5 rounded-full self-end`}
                                >
                                  {promo.badge}
                                </span>

                                <div className={promo.textColor}>
                                  <h5 className="font-bold text-[10px] leading-tight mb-0.5">
                                    {promo.title}
                                  </h5>
                                  <p className="text-sm font-extrabold leading-tight">
                                    {promo.subtitle}
                                  </p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div> */}
                    </div>
                  </div>
                ) : null}
              </div>
            ))}
          </nav>
        </div>
      </div>
    </div>
  );
};

export default Categories;
